import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Timeline, { TimelineHeaders, SidebarHeader, DateHeader, CustomMarker } from 'react-calendar-timeline';
import moment from 'moment';
import 'moment/locale/ko';
import { useNavigate, useLocation } from 'react-router-dom';
import { BsSearch, BsChevronDown, BsChevronRight, BsCalendarCheck } from "react-icons/bs";
import { compatApi } from '../../../utils/Intercepter';
import useTokenStore from '../../../stores/tokens';
import { toast } from 'react-toastify';
import '../../../styles/GanttTimeline.css';
import { PURPLE } from '../../../utils/designUtils';
import { useIsMobile } from '../../../utils/responsive';

moment.locale('ko');

const toMoment = (dateVal) => {
    if (!dateVal) return null;
    if (Array.isArray(dateVal)) return moment([dateVal[0], dateVal[1] - 1, dateVal[2]]);
    return moment(dateVal);
};

const getTimestamp = (dateVal) => toMoment(dateVal)?.valueOf() || null;

// [추가] 종료일(day)은 그 날짜의 자정(00:00)까지만 포함하는 것으로 취급되어 막대가 월 종료 구분선보다
// 하루 일찍 끝나 보이는 문제가 있었다. 종료 시점을 다음날 자정으로 잡아 그 날짜 전체를 막대에 포함시킨다.
const getEndTimestamp = (dateVal) => {
    const m = toMoment(dateVal);
    return m ? m.add(1, 'day').valueOf() : null;
};

const GanttChartPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useTokenStore();
    const organizationCode = user?.currentOrganizationCode || '';

    const [allProjects, setAllProjects] = useState([]);
    const [userProjectList, setUserProjectList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [groupMode, setGroupMode] = useState('project');

    const isMobile = useIsMobile(1080);

    // 모바일(≤1080px) 초기값: 월 단위 / 12개월 범위 → 컬럼 폭 확보
    const [zoomMode, setZoomMode] = useState(() => window.innerWidth <= 1080 ? 'month' : 'month');
    const zoomModeRef = useRef(window.innerWidth <= 1080 ? 'month' : 'month');

    const [visibleTimeStart, setVisibleTimeStart] = useState(() =>
        window.innerWidth <= 1080
            ? moment().subtract(6, 'months').valueOf()
            : moment().subtract(6, 'months').valueOf()
    );
    const [visibleTimeEnd, setVisibleTimeEnd] = useState(() =>
        window.innerWidth <= 1080
            ? moment().add(6, 'months').valueOf()
            : moment().add(6, 'months').valueOf()
    );

    const [selectedItemId, setSelectedItemId] = useState(null);

    const [showUserModal, setShowUserModal] = useState(false);
    const [modalProject, setModalProject] = useState({
        name: '', id: null, baseS: null, baseE: null, planS: null, planE: null, isActualBase: false
    });
    const [modalUsers, setModalUsers] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);

    const [selectedEmployeeEmails, setSelectedEmployeeEmails] = useState(new Set());
    const [showEmployeeModal, setShowEmployeeModal] = useState(false);
    const [modalEmployeeSearch, setModalEmployeeSearch] = useState('');

    const [showShareModal, setShowShareModal] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [shareLoading, setShareLoading] = useState(false);
    const [shareCopied, setShareCopied] = useState(false);

    const fetchGanttData = useCallback(async () => {
        if (!organizationCode) { setLoading(false); return; }
        setLoading(true);
        try {
            const { ok, data } = await compatApi.get(`/project/gantt-data?organizationCode=${organizationCode}`);
            if (ok) {
                const sorted = (Array.isArray(data) ? data : []).sort((a, b) => {
                    const dateA = a.contractualStartDate || a.startDate || "";
                    const dateB = b.contractualStartDate || b.startDate || "";
                    return String(dateB).localeCompare(String(dateA));
                });
                setAllProjects(sorted);
            }
        } catch (e) { toast.error('데이터 로드 실패'); }
        finally { setLoading(false); }

        try {
            const { ok, data } = await compatApi.get(`/project/allUserProjectList?organizationCode=${organizationCode}`);
            if (ok) {
                const list = Array.isArray(data) ? data : [];
                setUserProjectList(list);
            }
        } catch (e) { toast.error('데이터 로드 실패'); }
        finally { setLoading(false); }
    }, [organizationCode]);

    useEffect(() => { fetchGanttData(); }, [fetchGanttData]);

    const handleZoom = (unit) => {
        setZoomMode(unit);
        zoomModeRef.current = unit;
        const center = (visibleTimeStart + visibleTimeEnd) / 2;
        let newStart, newEnd;
        if (unit === 'year') {
            // 모바일에서는 2년(24개월), 데스크톱에서는 4년(48개월)
            const range = isMobile ? 1 : 2;
            newStart = moment(center).subtract(range, 'years').valueOf();
            newEnd = moment(center).add(range, 'years').valueOf();
        } else {
            newStart = moment(center).subtract(6, 'months').valueOf();
            newEnd = moment(center).add(6, 'months').valueOf();
        }
        setVisibleTimeStart(newStart);
        setVisibleTimeEnd(newEnd);
    };

    const moveToProject = useCallback((project) => {
        const start = getTimestamp(project.contractualStartDate) || getTimestamp(project.startDate);
        if (start) {
            let newStart;
            if (zoomModeRef.current === 'year') {
                newStart = moment(start).subtract(1, 'years').valueOf();
            } else {
                newStart = moment(start).subtract(1, 'months').valueOf();
            }
            let newEnd;
            if (zoomModeRef.current === 'year') {
                newEnd = moment(newStart).add(4, 'years').valueOf();
            } else {
                newEnd = moment(newStart).add(1, 'years').valueOf();
            }
            setVisibleTimeStart(newStart);
            setVisibleTimeEnd(newEnd);
        }
    }, []);

    useEffect(() => {
        if (loading) return;
        if (!allProjects.length) return;
        const focusProjectId = location.state?.projectId;
        if (focusProjectId) {
            const projectToFocus = allProjects.find(p => String(p.projectId) === String(focusProjectId));
            if (projectToFocus) setTimeout(() => moveToProject(projectToFocus), 100);
            return;
        }
        if (groupMode === 'user') {
            const today = moment();
            let start, end;
            if (zoomModeRef.current === 'year') {
                start = today.clone().subtract(2, 'years').valueOf();
                end = today.clone().add(2, 'years').valueOf();
            } else {
                start = today.clone().subtract(6, 'months').valueOf();
                end = today.clone().add(6, 'months').valueOf();
            }
            setVisibleTimeStart(start);
            setVisibleTimeEnd(end);
        }
    }, [loading, allProjects, location.state, moveToProject, groupMode]);

    const openPersonnelModal = async (projectId, projectName) => {
        const target = allProjects.find(p => String(p.projectId) === String(projectId));
        if (!target) return;
        let baseS = getTimestamp(target.startDate) || getTimestamp(target.contractualStartDate);
        let baseE = getTimestamp(target.endDate) || getTimestamp(target.contractualEndDate);
        let isActualBase = !!(getTimestamp(target.startDate) && getTimestamp(target.endDate));
        setModalProject({
            name: projectName, id: projectId, baseS, baseE,
            planS: getTimestamp(target.contractualStartDate),
            planE: getTimestamp(target.contractualEndDate),
            isActualBase,
            projectType: target.projectType
        });
        setShowUserModal(true);
        setModalLoading(true);
        try {
            const { ok, data } = await compatApi.get(`/project/${projectId}/personnel`);
            if (ok) setModalUsers(data || []);
        } catch (e) { toast.error('인력 정보 조회 실패'); }
        finally { setModalLoading(false); }
    };

    // 간트 막대 클릭 시 프로젝트 상세(인력) 모달 열기. 직원별 뷰는 막대 id가 projectInfoId라 행에서 projectId를 역추적
    const handleGanttItemActivate = (itemId) => {
        const id = itemId.toString().includes('_') ? itemId.split('_')[1] : itemId;
        if (groupMode === 'user') {
            const row = userProjectList.find(r => String(r.projectInfoId) === String(id));
            if (row && row.projectId) openPersonnelModal(row.projectId, row.projectName);
            return;
        }
        const proj = allProjects.find(x => String(x.projectId) === String(id));
        if (proj) openPersonnelModal(proj.projectId, proj.projectName);
    };

    const buildUserGroupsAndItems = (list) => {
        const groups = [];
        const items = [];
        const userMap = {};
        const now = Date.now();

        list.forEach(row => {
            const userKey = row.userEmail;
            if (!userMap[userKey]) {
                userMap[userKey] = true;
                const hasEngineer = row.certificates && row.certificates.includes("정보처리기사") ? "보유" : "미보유";
                groups.push({
                    id: userKey,
                    title: (
                        <div style={{ display: 'flex', flexDirection: 'column', padding: '8px 0 8px 12px', width: '100%', lineHeight: 1.4 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#212529' }}>{row.userName}</div>
                            <div style={{ fontSize: 10.5, fontWeight: 'normal', color: '#6c757d', textAlign: 'left' }}>
                                학력 <span style={{ color: '#007bff' }}>{row.eduGrade || '초급'}</span> · 기술 <span style={{ color: '#007bff' }}>{row.techGrade || '초급'}</span>
                            </div>
                            <div style={{ fontSize: 10.5, fontWeight: 'normal', color: '#6c757d', textAlign: 'left' }}>
                                {row.careerDisplay || '경력 없음'} · 정처기 <span style={{ color: hasEngineer === '미보유' ? '#dc3545' : '#007bff' }}>{hasEngineer}</span>
                            </div>
                        </div>
                    )
                });
            }

            const planS = getTimestamp(row.startDate);
            const planE = getEndTimestamp(row.endDate);
            const actS = getTimestamp(row.actualStartDate);
            const actE = getEndTimestamp(row.actualEndDate);
            const isMaintenance = row.projectType === 'MAINTENANCE';

            if (isMaintenance) {
                // 유지보수 프로젝트: 단일 막대에 그라데이션(시작일~오늘 초록=완료, 오늘~종료일 회색=남은 일정)으로 표시
                const mS = actS || planS;
                const mEnd = planE || actE;
                if (mS && mEnd && mEnd > mS) {
                    const pct = Math.max(0, Math.min(100, ((now - mS) / (mEnd - mS)) * 100));
                    items.push({
                        id: `act_${row.projectInfoId}`, group: userKey, start_time: mS, end_time: mEnd,
                        title: row.projectName,
                        itemProps: { style: { background: `linear-gradient(to right, #5FC972 ${pct}%, #D1D5DB ${pct}%)`, color: '#222', height: '16px', borderRadius: '4px', fontSize: '10px', fontWeight: 700, zIndex: 41 } }
                    });
                }
            } else if (actS || actE) {
                const realStart = actS || planS;
                const realEnd = actE || planE;
                if (realStart && realEnd && realStart < realEnd) {
                    items.push({
                        id: `act_${row.projectInfoId}`,
                        group: userKey,
                        start_time: realStart,
                        end_time: realEnd,
                        title: row.projectName,
                        itemProps: {
                            style: {
                                background: actS && actE ? '#5FC972' : '#F59E0B',
                                color: '#222', height: '16px', borderRadius: '4px',
                                fontSize: '10px', fontWeight: 700, zIndex: 41
                            }
                        }
                    });
                }
            }
        });

        return { groups, items };
    };

    const uniqueEmployees = useMemo(() => {
        const map = {};
        userProjectList.forEach(row => {
            if (!map[row.userEmail]) {
                map[row.userEmail] = { userEmail: row.userEmail, userName: row.userName, eduGrade: row.eduGrade, techGrade: row.techGrade };
            }
        });
        return Object.values(map).sort((a, b) => (a.userName || '').localeCompare(b.userName || ''));
    }, [userProjectList]);

    const visibleEmployeesInModal = useMemo(() => {
        if (!modalEmployeeSearch) return uniqueEmployees;
        const lower = modalEmployeeSearch.toLowerCase();
        return uniqueEmployees.filter(e => (e.userName || '').toLowerCase().includes(lower));
    }, [uniqueEmployees, modalEmployeeSearch]);

    const filteredUserProjectList = useMemo(() => {
        if (selectedEmployeeEmails.size === 0) return userProjectList;
        return userProjectList.filter(row => selectedEmployeeEmails.has(row.userEmail));
    }, [userProjectList, selectedEmployeeEmails]);

    const toggleEmployee = (email) => {
        setSelectedEmployeeEmails(prev => {
            const next = new Set(prev);
            if (next.has(email)) next.delete(email);
            else next.add(email);
            return next;
        });
    };

    const handleCreateShareLink = async () => {
        if (selectedEmployeeEmails.size === 0) { toast.warn('공유할 직원을 먼저 선택하세요.'); return; }
        setShareLoading(true);
        try {
            const response = await compatApi.post('/gantt-shares', {
                organizationCode,
                employeeEmails: Array.from(selectedEmployeeEmails),
            });
            if (response.ok) {
                const { shareId } = response.data;
                const url = `${window.location.origin}/gantt/shared/${shareId}`;
                setShareUrl(url);
                setShowShareModal(true);
            } else {
                toast.error('공유 링크 생성에 실패했습니다.');
            }
        } catch {
            toast.error('공유 링크 생성 중 오류가 발생했습니다.');
        } finally {
            setShareLoading(false);
        }
    };

    const handleCopyShareUrl = () => {
        navigator.clipboard.writeText(shareUrl).then(() => {
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
        });
    };

    const { groups, items } = useMemo(() => {
        if (groupMode === 'user') return buildUserGroupsAndItems(filteredUserProjectList);

        const g = []; const i = []; const groupsByYear = {};
        const now = Date.now();
        allProjects.forEach(proj => {
            if (!proj.projectName.toLowerCase().includes(searchTerm.toLowerCase())) return;
            const startYear = proj.contractualStartDate ? toMoment(proj.contractualStartDate).year() : "기타";
            if (!groupsByYear[startYear]) groupsByYear[startYear] = [];
            groupsByYear[startYear].push(proj);
        });

        Object.keys(groupsByYear).sort((a, b) => b - a).forEach(year => {
            g.push({ id: `year-${year}`, title: String(year), isYearHeader: true, className: 'year-row-highlight' });
            if (!collapsedGroups[year]) {
                groupsByYear[year].forEach(p => {
                    g.push({ id: p.projectId, title: p.projectName, originalData: p });
                    const pS = getTimestamp(p.contractualStartDate);
                    const pE = getEndTimestamp(p.contractualEndDate || p.endDate);
                    const aS = getTimestamp(p.startDate);
                    const aE = getEndTimestamp(p.endDate);
                    const isMaintenance = p.projectType === 'MAINTENANCE';
                    // [추가] 계약 연장 이력: 완료/진행중 막대(있으면 그 막대, 없으면 계획기간 막대) 뒤에 시간·모양 모두 이어 붙여 하나의 막대처럼 보이게 함
                    const validExtensions = (p.contractExtensions || [])
                        .map(extension => ({
                            start: getTimestamp(extension.extensionStartDate),
                            end: getEndTimestamp(extension.extensionEndDate),
                        }))
                        .filter(ext => ext.start && ext.end);
                    const hasExtensions = validExtensions.length > 0;
                    const hasProgressBar = isMaintenance || !!(aS || aE);
                    if (pS && pE) i.push({
                        id: `plan_${p.projectId}`, group: p.projectId, start_time: pS, end_time: pE,
                        title: !(aS || aE) && !isMaintenance ? p.projectName : '',
                        className: selectedItemId === `plan_${p.projectId}` ? 'gantt-item-selected' : '',
                        itemProps: { style: { background: '#77BEF0', color: '#fff', height: '32px', zIndex: 40, borderTopLeftRadius: '6px', borderBottomLeftRadius: '6px', borderTopRightRadius: (hasExtensions && !hasProgressBar) ? 0 : '6px', borderBottomRightRadius: (hasExtensions && !hasProgressBar) ? 0 : '6px', borderTopWidth: 0, borderBottomWidth: 0, borderLeftWidth: 0, borderRightWidth: 0, display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: '11px', fontWeight: '600' } }
                    });
                    // [수정] 연장 막대는 계획기간(32px) 막대가 아닌 완료/진행중(18px, top +7px) 막대와 top이 맞도록 위치·높이를 맞추고,
                    // 색상도 본래 프로젝트 상태(완료=초록/진행중=주황)를 그대로 따르도록 함. 색이 같아 구분이 안 되므로
                    // 원래 일정과 연장이 갈리는 첫 지점(왼쪽)에만 검은 막대로 경계를 표시.
                    // [주의] GanttTimeline.css의 `.rct-item { border: none !important; box-shadow: none !important; }` 규칙 때문에
                    // border나 box-shadow로는 그릴 수 없어(인라인 스타일이 !important에 막힘), 유지보수 막대와 동일하게
                    // background의 linear-gradient로 검은 띠를 그린다.
                    const extensionColor = (aS && aE) ? '#5FC972' : '#F59E0B';
                    const EXT_MARKER_WIDTH = 1; // px, 검은 경계 막대 두께
                    if (isMaintenance) {
                        // 유지보수 프로젝트: 단일 막대에 그라데이션(시작일~오늘 초록=완료, 오늘~종료일 회색=남은 일정)으로 표시해 프로젝트 명이 잘리지 않게 함
                        const mS = aS || pS;
                        const mEnd = pE || aE;
                        if (mS && mEnd && mEnd > mS) {
                            const pct = Math.max(0, Math.min(100, ((now - mS) / (mEnd - mS)) * 100));
                            i.push({
                                id: `act_${p.projectId}`, group: p.projectId, start_time: mS, end_time: mEnd,
                                title: p.projectName,
                                className: selectedItemId === `act_${p.projectId}` ? 'gantt-item-selected' : '',
                                itemProps: { style: { background: `linear-gradient(to right, #5FC972 ${pct}%, #D1D5DB ${pct}%)`, color: '#222', height: '18px', marginTop: '7px', zIndex: 41, borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px', borderTopRightRadius: hasExtensions ? 0 : '4px', borderBottomRightRadius: hasExtensions ? 0 : '4px', borderTopWidth: 0, borderBottomWidth: 0, borderLeftWidth: 0, borderRightWidth: 0, display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: '10px', fontWeight: '600' } }
                            });
                        }
                    } else if (aS || aE) i.push({
                        id: `act_${p.projectId}`, group: p.projectId, start_time: aS || pS, end_time: aE || pE,
                        title: p.projectName,
                        className: selectedItemId === `act_${p.projectId}` ? 'gantt-item-selected' : '',
                        itemProps: { style: { background: (aS && aE) ? '#5FC972' : '#F59E0B', color: '#222', height: '18px', marginTop: '7px', zIndex: 41, borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px', borderTopRightRadius: hasExtensions ? 0 : '4px', borderBottomRightRadius: hasExtensions ? 0 : '4px', borderTopWidth: 0, borderBottomWidth: 0, borderLeftWidth: 0, borderRightWidth: 0, display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: '10px', fontWeight: '600' } }
                    });
                    // [수정] 완료/진행중(또는 유지보수) 막대보다 항상 나중에 push해서 항상 위에 그려지도록 함.
                    // 실제 종료일이 연장 시작일 이후로 잡혀있는 등 시간이 겹치는 경우, 먼저 push된 쪽이 나중 push된
                    // 쪽에 덮여 보이지 않았던 문제(같은 zIndex라도 DOM 순서상 나중 요소가 위에 그려짐)를 해결.
                    validExtensions.forEach((extension, extIdx) => {
                        const isFirstExtension = extIdx === 0;
                        const isLastExtension = extIdx === validExtensions.length - 1;
                        const extItemId = `ext_${p.projectId}_${extIdx}`;
                        i.push({
                            id: extItemId, group: p.projectId, start_time: extension.start, end_time: extension.end,
                            title: '연장',
                            className: selectedItemId === extItemId ? 'gantt-item-selected' : '',
                            itemProps: { style: { background: isFirstExtension
                                ? `linear-gradient(to right, #000 0, #000 ${EXT_MARKER_WIDTH}px, ${extensionColor} ${EXT_MARKER_WIDTH}px, ${extensionColor} 100%)`
                                : extensionColor, color: '#222', height: '18px', marginTop: '7px', zIndex: 41, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: isLastExtension ? '4px' : 0, borderBottomRightRadius: isLastExtension ? '4px' : 0, borderTopWidth: 0, borderBottomWidth: 0, borderLeftWidth: 0, borderRightWidth: 0, display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: '10px', fontWeight: '600' } }
                        });
                    });
                });
            }
        });
        return { groups: g, items: i };
    }, [allProjects, searchTerm, collapsedGroups, selectedItemId, groupMode, filteredUserProjectList]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const existingStyle = document.getElementById('dynamic-gantt-style');
            if (existingStyle) existingStyle.remove();
            let styleRules = '';
            groups.forEach((group, index) => {
                if (group.isYearHeader) {
                    const pos = index + 1;
                    styleRules += `
                        .rct-sidebar .rct-group:nth-child(${pos}) { border-top: 2px solid #bbb !important; background-color: #f1f3f5 !important; font-weight: bold !important; }
                        .rct-horizontal-lines > .rct-hl-even:nth-child(${pos}), .rct-horizontal-lines > .rct-hl-odd:nth-child(${pos}) { border-top: 2px solid #bbb !important; background-color: rgba(241, 243, 245, 0.6) !important; }
                        .rct-items > .rct-items-row:nth-child(${pos}) { border-top: 2px solid #bbb !important; }
                    `;
                }
            });
            if (styleRules) {
                const style = document.createElement('style');
                style.id = 'dynamic-gantt-style';
                style.innerHTML = styleRules;
                document.head.appendChild(style);
            }
        }, 200);
        return () => {
            clearTimeout(timer);
            const existingStyle = document.getElementById('dynamic-gantt-style');
            if (existingStyle) existingStyle.remove();
        };
    }, [groups, searchTerm, collapsedGroups, loading, groupMode]);

    const PersonnelItem = ({ user, planS, planE, projectType }) => {
        const actualEnd = user.actualEndDate || (projectType === 'MAINTENANCE' && user.actualStartDate ? Date.now() : null);

        // 3개 바(프로젝트 계획·투입 계획·실제) 전체 날짜 중 최솟값·최댓값을 base로 사용
        const allStarts = [planS, getTimestamp(user.startDate), getTimestamp(user.actualStartDate)].filter(Boolean);
        const allEnds   = [planE, getTimestamp(user.endDate),   actualEnd ? getTimestamp(actualEnd) : null].filter(Boolean);
        const baseS = allStarts.length ? Math.min(...allStarts) : null;
        const baseE = allEnds.length   ? Math.max(...allEnds)   : null;

        const calc = (start, end) => {
            if (!start || !end || !baseS || !baseE) return { display: 'none' };
            const s = getTimestamp(start);
            const e = getTimestamp(end);
            const total = baseE - baseS;
            if (total <= 0 || e <= baseS || s >= baseE) return { display: 'none' };
            const leftPct  = Math.max(0, Math.min(100, ((s - baseS) / total) * 100));
            const rightPct = Math.max(0, Math.min(100, ((e - baseS) / total) * 100));
            const widthPct = rightPct - leftPct;
            if (widthPct <= 0) return { display: 'none' };
            return { position: 'absolute', left: `${leftPct}%`, width: `${widthPct}%` };
        };
        return (
            <div style={{ padding: '12px', border: '1px solid #f0eff5', borderRadius: 10, marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                        <span style={{ fontWeight: 700, fontSize: 15, marginRight: 8 }}>{user.userName}</span>
                        <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 999, background: '#f5f4fa', border: '1px solid #e5e0f7', color: '#4b4869' }}>{user.skillLevel}</span>
                        <small style={{ marginLeft: 8, color: '#8b8a99' }}>{user.assignments}</small>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11, lineHeight: 1.4 }}>
                        <div style={{ color: '#007bff', fontWeight: 700 }}>계획: {toMoment(user.startDate).format('YYYY-MM-DD')} ~ {toMoment(user.endDate).format('YYYY-MM-DD')}</div>
                        <div style={{ color: '#28a745', fontWeight: 700 }}>실적: {user.actualStartDate ? toMoment(user.actualStartDate).format('YYYY-MM-DD') : '-'} ~ {user.actualEndDate ? toMoment(user.actualEndDate).format('YYYY-MM-DD') : '-'}</div>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, background: 'white', padding: 4, border: '1px solid #eee', borderRadius: 4 }}>
                    <div style={{ position: 'relative', height: 8, width: '100%', background: '#f8f9fa', overflow: 'hidden', borderRadius: 4 }}><div style={{ ...calc(planS, planE), height: '100%', backgroundColor: '#F49BAB' }} /></div>
                    <div style={{ position: 'relative', height: 8, width: '100%', background: '#f8f9fa', overflow: 'hidden', borderRadius: 4 }}><div style={{ ...calc(user.startDate, user.endDate), height: '100%', backgroundColor: '#77BEF0' }} /></div>
                    <div style={{ position: 'relative', height: 8, width: '100%', background: '#f8f9fa', overflow: 'hidden', borderRadius: 4 }}><div style={{ ...calc(user.actualStartDate, actualEnd), height: '100%', backgroundColor: '#8ABB6C' }} /></div>
                </div>
            </div>
        );
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 61px)' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #e5e0f7', borderTopColor: PURPLE, animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    const btnGroup = (active) => ({
        height: 32, padding: '0 12px', border: `1.5px solid ${active ? PURPLE : '#e5e0f7'}`,
        background: active ? PURPLE : 'white', color: active ? 'white' : '#4b4869',
        fontWeight: active ? 700 : 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
    });

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)', padding: '24px clamp(16px, 2.5vw, 32px) 40px' }}>
            <div style={{
                background: 'white', borderRadius: 18,
                border: '1px solid #f0eff5',
                boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                padding: '16px 20px',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, padding: 12, borderRadius: 10, border: '1px solid #f0eff5', background: '#faf9fe', boxShadow: '0 1px 4px rgba(60,50,120,.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ display: 'flex', border: '1.5px solid #e5e0f7', borderRadius: 8, overflow: 'hidden', height: 32 }}>
                            <span style={{ padding: '0 10px', background: '#f5f4fa', display: 'flex', alignItems: 'center', borderRight: '1.5px solid #e5e0f7', color: '#8b8a99' }}><BsSearch size={13} /></span>
                            <input placeholder="프로젝트명 검색" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ border: 'none', outline: 'none', padding: '0 10px', fontSize: 13, fontFamily: 'inherit', width: 220, boxShadow: 'none' }} />
                        </div>
                        <div style={{ width: 1, height: 20, background: '#e5e0f7' }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Legend item={{ color: '#77BEF0', text: '계획기간' }} />
                            <Legend item={{ color: '#5FC972', text: '완료' }} />
                            <Legend item={{ color: '#F59E0B', text: '진행중' }} />
                            <Legend item={{ color: '#000000', text: '연장 시작' }} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {groupMode === 'user' && (
                            <>
                                <button onClick={() => setShowEmployeeModal(true)} style={{ ...btnGroup(false), position: 'relative' }}>
                                    직원 필터
                                    {selectedEmployeeEmails.size > 0 && (
                                        <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 999, background: PURPLE, color: 'white' }}>{selectedEmployeeEmails.size}명</span>
                                    )}
                                </button>
                                <button
                                    onClick={handleCreateShareLink}
                                    disabled={shareLoading || selectedEmployeeEmails.size === 0}
                                    style={{ ...btnGroup(selectedEmployeeEmails.size > 0), opacity: selectedEmployeeEmails.size === 0 ? 0.5 : 1 }}
                                    title={selectedEmployeeEmails.size === 0 ? '직원을 먼저 선택하세요' : '선택 직원 기준 공유 링크 생성'}
                                >
                                    {shareLoading ? '처리중...' : '공유 링크 생성'}
                                </button>
                            </>
                        )}
                        <div style={{ display: 'flex' }}>
                            <button onClick={() => setGroupMode('project')} style={{ ...btnGroup(groupMode === 'project'), borderRadius: '8px 0 0 8px' }}>프로젝트별</button>
                            <button onClick={() => setGroupMode('user')} style={{ ...btnGroup(groupMode === 'user'), borderRadius: '0 8px 8px 0', borderLeft: 'none' }}>직원별</button>
                        </div>
                        <div style={{ display: 'flex' }}>
                            <button onClick={() => handleZoom('year')} style={{ ...btnGroup(zoomMode === 'year'), borderRadius: '8px 0 0 8px' }}>연 단위</button>
                            <button onClick={() => handleZoom('month')} style={{ ...btnGroup(zoomMode === 'month'), borderRadius: '0 8px 8px 0', borderLeft: 'none' }}>월 단위</button>
                        </div>
                        <button onClick={() => {
                            const today = moment();
                            let newStart, newEnd;
                            if (zoomModeRef.current === 'year') {
                                const range = isMobile ? 1 : 2;
                                newStart = today.clone().subtract(range, 'years').valueOf();
                                newEnd = today.clone().add(range, 'years').valueOf();
                            } else {
                                newStart = today.clone().subtract(6, 'months').valueOf();
                                newEnd = today.clone().add(6, 'months').valueOf();
                            }
                            setVisibleTimeStart(newStart);
                            setVisibleTimeEnd(newEnd);
                        }} style={{ height: 32, padding: '0 14px', borderRadius: 8, border: 'none', background: '#1f1d2e', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Today</button>
                        {user?.currentRole === 'MASTER' && (
                            <>
                                <button onClick={() => navigate('/project/create')} style={{ ...btnGroup(false) }}>프로젝트 추가</button>
                                <button onClick={() => navigate('/project/list')} style={{ ...btnGroup(false), color: '#16a34a', borderColor: '#bbf7d0' }}>프로젝트 목록으로</button>
                            </>
                        )}
                    </div>
                </div>

                <div style={{ border: '1px solid #e5e0f7', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(60,50,120,.06)' }}>
                    <Timeline
                        className={groupMode === 'user' ? 'user-mode' : 'project-mode'}
                        groups={groups} items={items}
                        stackItems={groupMode === 'user'}
                        visibleTimeStart={visibleTimeStart} visibleTimeEnd={visibleTimeEnd}
                        onTimeChange={(s, e) => { setVisibleTimeStart(s); setVisibleTimeEnd(e); }}
                        // 직원별 보기는 이름 아래 부가정보가 2줄로 늘어나 행 높이를 넉넉하게 확보해야 가려지지 않는다
                        lineHeight={groupMode === 'user' ? 82 : 55}
                        sidebarWidth={isMobile ? 120 : 280} canMove={false} canResize={false}
                        onItemSelect={handleGanttItemActivate}
                        onItemClick={handleGanttItemActivate}
                        onCanvasClick={() => setSelectedItemId(null)}
                        groupRenderer={({ group }) => group.isYearHeader ? (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', padding: '0 12px', fontWeight: 700, cursor: 'pointer', fontSize: 14, background: '#f1f3f5', borderTop: '2px solid #bbb', borderBottom: '1px solid #ddd', boxSizing: 'border-box' }}
                                onClick={() => setCollapsedGroups(p => ({ ...p, [group.title]: !p[group.title] }))}>
                                {collapsedGroups[group.title] ? <BsChevronRight style={{ marginRight: 8 }} /> : <BsChevronDown style={{ marginRight: 8 }} />} {group.title}년
                            </div>
                        ) : (
                            <div style={{ paddingLeft: 16, paddingTop: 8, paddingBottom: 8, cursor: 'pointer' }}
                                onClick={e => {
                                    if (groupMode === 'user') { e.preventDefault(); e.stopPropagation(); return; }
                                    setSelectedItemId(null); moveToProject(group.originalData);
                                }}
                                onDoubleClick={() => openPersonnelModal(group.id, group.title)}
                            >
                                <span style={{ fontSize: 13, fontWeight: 500, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.title}</span>
                            </div>
                        )}
                    >
                        <TimelineHeaders>
                            <SidebarHeader>{({ getRootProps }) => <div {...getRootProps()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, background: '#f5f4fa', borderBottom: '1px solid #e5e0f7', borderRight: '1px solid #e5e0f7', fontSize: isMobile ? 11 : 13, width: isMobile ? 120 : 280, color: '#1f1d2e' }}>{groupMode === 'user' ? '직원 목록' : '프로젝트 목록'}</div>}</SidebarHeader>
                            <DateHeader unit="primaryHeader" format="YYYY년" />
                            <DateHeader unit="month" labelFormat="M월" />
                        </TimelineHeaders>
                        <CustomMarker date={Date.now()}>{({ styles }) => <div style={{ ...styles, backgroundColor: '#dc3545', width: '2px', zIndex: 100 }} />}</CustomMarker>
                    </Timeline>
                </div>

                {/* 직원 선택 모달 */}
                {showEmployeeModal && (
                    <div onClick={() => { setShowEmployeeModal(false); setModalEmployeeSearch(''); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                        <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: 'calc(100vh - 61px)', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0eff5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 700, fontSize: 15, color: '#1f1d2e' }}>직원 선택</span>
                                <button onClick={() => { setShowEmployeeModal(false); setModalEmployeeSearch(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#8b8a99', lineHeight: 1 }}>×</button>
                            </div>
                            <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                                <input placeholder="이름 검색..." value={modalEmployeeSearch} onChange={(e) => setModalEmployeeSearch(e.target.value)} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #e5e0f7', padding: '0 10px', fontSize: 13, outline: 'none', marginBottom: 12, boxSizing: 'border-box', fontFamily: 'inherit', boxShadow: 'none' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <button onClick={() => setSelectedEmployeeEmails(new Set(uniqueEmployees.map(e => e.userEmail)))} style={{ height: 30, padding: '0 12px', borderRadius: 6, border: `1.5px solid ${PURPLE}`, background: 'white', color: PURPLE, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>전체 선택</button>
                                    <button onClick={() => setSelectedEmployeeEmails(new Set())} style={{ height: 30, padding: '0 12px', borderRadius: 6, border: '1.5px solid #d1d5db', background: 'white', color: '#6b7280', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>선택 초기화</button>
                                </div>
                                {selectedEmployeeEmails.size > 0 && (
                                    <div style={{ marginBottom: 12, padding: 8, borderRadius: 8, border: '1px solid #e5e0f7', background: '#faf9fe' }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#8b8a99', marginBottom: 6 }}>선택된 직원 ({selectedEmployeeEmails.size}명)</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                            {uniqueEmployees.filter(e => selectedEmployeeEmails.has(e.userEmail)).map(emp => (
                                                <span key={emp.userEmail} onClick={() => toggleEmployee(emp.userEmail)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 'normal', cursor: 'pointer', padding: '4px 8px', borderRadius: 999, background: PURPLE, color: 'white' }} title="클릭하여 선택 해제">
                                                    {emp.userName}<span style={{ fontSize: 10, opacity: 0.8 }}>✕</span>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                                    {visibleEmployeesInModal.length === 0 ? (
                                        <p style={{ textAlign: 'center', color: '#8b8a99', fontSize: 13, padding: '24px 0' }}>검색 결과가 없습니다.</p>
                                    ) : (
                                        visibleEmployeesInModal.map(emp => {
                                            const isSelected = selectedEmployeeEmails.has(emp.userEmail);
                                            return (
                                                <div key={emp.userEmail} onClick={() => toggleEmployee(emp.userEmail)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px', marginBottom: 4, borderRadius: 8, cursor: 'pointer', background: isSelected ? '#e8f0fe' : 'transparent', border: isSelected ? '1px solid #a8c4f8' : '1px solid transparent' }}>
                                                    <div style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0, border: isSelected ? `2px solid ${PURPLE}` : '2px solid #adb5bd', background: isSelected ? PURPLE : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {isSelected && <span style={{ color: '#fff', fontSize: 11, lineHeight: 1 }}>✓</span>}
                                                    </div>
                                                    <span style={{ fontSize: 14, fontWeight: isSelected ? 600 : 400, color: isSelected ? PURPLE : '#212529' }}>
                                                        {emp.userName}<small style={{ marginLeft: 4, color: '#6c757d', fontWeight: 400 }}>(학력 {emp.eduGrade || '초급'} · 기술 {emp.techGrade || '초급'})</small>
                                                    </span>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                            <div style={{ padding: '12px 20px', borderTop: '1px solid #f0eff5' }}>
                                <button onClick={() => { setShowEmployeeModal(false); setModalEmployeeSearch(''); }} style={{ height: 36, padding: '0 18px', borderRadius: 8, border: 'none', background: PURPLE, color: 'white', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                                    확인 {selectedEmployeeEmails.size > 0 && `(${selectedEmployeeEmails.size}명 선택)`}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 공유 링크 모달 */}
                {showShareModal && (
                    <div onClick={() => { setShowShareModal(false); setShareCopied(false); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                        <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0eff5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 700, fontSize: 15, color: '#1f1d2e' }}>공유 링크 생성 완료</span>
                                <button onClick={() => { setShowShareModal(false); setShareCopied(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#8b8a99', lineHeight: 1 }}>×</button>
                            </div>
                            <div style={{ padding: '16px 20px' }}>
                                <p style={{ color: '#8b8a99', fontSize: 13, marginBottom: 10 }}>아래 링크로 접속하면 로그인 없이 선택된 직원의 간트차트를 볼 수 있습니다.</p>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input readOnly value={shareUrl} onClick={(e) => e.target.select()} style={{ flex: 1, height: 36, borderRadius: 8, border: '1.5px solid #e5e0f7', padding: '0 10px', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxShadow: 'none' }} />
                                    <button onClick={handleCopyShareUrl} style={{ height: 36, padding: '0 14px', borderRadius: 8, border: `1.5px solid ${shareCopied ? '#16a34a' : PURPLE}`, background: shareCopied ? '#16a34a' : 'white', color: shareCopied ? 'white' : PURPLE, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                        {shareCopied ? '복사됨!' : '복사'}
                                    </button>
                                </div>
                            </div>
                            <div style={{ padding: '12px 20px', borderTop: '1px solid #f0eff5', display: 'flex', justifyContent: 'flex-end' }}>
                                <button onClick={() => { setShowShareModal(false); setShareCopied(false); }} style={{ height: 36, padding: '0 16px', borderRadius: 8, border: '1.5px solid #d1d5db', background: 'white', color: '#6b7280', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>닫기</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 인력 현황 모달 */}
                {showUserModal && (
                    <div onClick={() => setShowUserModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                        <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 700, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0eff5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 700, fontSize: 15, color: '#1f1d2e' }}>인력별 참여 투입 현황</span>
                                <button onClick={() => setShowUserModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#8b8a99', lineHeight: 1 }}>×</button>
                            </div>
                            <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                                <div style={{ marginBottom: 12 }}>
                                    <h4 style={{ fontWeight: 700, color: PURPLE, marginBottom: 4, margin: 0 }}>{modalProject.name}</h4>
                                    <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#8b8a99', marginTop: 4 }}>
                                        <Legend item={{ color: '#F49BAB', text: '프로젝트 계획', isBox: true }} />
                                        <Legend item={{ color: '#77BEF0', text: '투입 계획', isBox: true }} />
                                        <Legend item={{ color: '#8ABB6C', text: '실제 투입', isBox: true }} />
                                    </div>
                                </div>
                                <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, background: '#faf9fe', border: '1px solid #e5e0f7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}><BsCalendarCheck style={{ color: '#16a34a' }} />차트 기준 기간 ({modalProject.isActualBase ? '실제' : '계획'} 기준)</div>
                                    <div style={{ fontSize: 12, color: '#1f1d2e', fontFamily: 'monospace' }}>{moment(modalProject.baseS).format('YYYY-MM-DD')} ~ {moment(modalProject.baseE).format('YYYY-MM-DD')}</div>
                                </div>
                                {modalLoading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                                        <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #e5e0f7', borderTopColor: PURPLE, animation: 'spin 0.8s linear infinite' }} />
                                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                                    </div>
                                ) : modalUsers.length > 0 ? (
                                    modalUsers.map((u, idx) => <PersonnelItem key={idx} user={u} {...modalProject} />)
                                ) : (
                                    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#8b8a99', border: '1px solid #f0eff5', borderRadius: 10 }}>참여 인원이 없습니다.</div>
                                )}
                            </div>
                            <div style={{ padding: '12px 20px', borderTop: '1px solid #f0eff5', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                <button onClick={() => navigate('/project/edit', { state: allProjects.find(p => String(p.projectId) === String(modalProject.id)) })} style={{ height: 36, padding: '0 16px', borderRadius: 8, border: 'none', background: PURPLE, color: 'white', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>상세페이지 이동</button>
                                <button onClick={() => setShowUserModal(false)} style={{ height: 36, padding: '0 16px', borderRadius: 8, border: '1.5px solid #d1d5db', background: 'white', color: '#6b7280', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>닫기</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const Legend = ({ item }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ backgroundColor: item.color, width: 12, height: 12, marginRight: 5, borderRadius: 2, border: item.isBox ? '1px solid #ccc' : 'none', flexShrink: 0 }}></div>
        <span style={{ fontSize: 12 }}>{item.text}</span>
    </div>
);

export default GanttChartPage;
